import { useState, useEffect } from 'react';
import { User, AuthResponse } from '../types';
import { authAPI, userAPI } from '../services/api';
import { socketService } from '../services/socket';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('buddy_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));

  useEffect(() => {
    const verifyTokenOnLoad = async () => {
      if (token && !user) {
        setLoading(true);
        try {
          await verifyToken(token);
        } catch (error) {
          console.error("Token verification failed on load", error);
        } finally {
          setLoading(false);
        }
      } else if (token && user) {
        socketService.connect(token, user.id || (user as any)._id);
        setLoading(false);
      } else {
        setLoading(false);
      }
    };
    verifyTokenOnLoad();
  }, [token, user]);

  const verifyToken = async (token: string) => {
    try {
      const userData = await authAPI.getProfile();
      setUser(userData);
      socketService.connect(token, userData.id);
    } catch (error) {
      localStorage.removeItem('token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<AuthResponse> => {
    try {
      const response = await authAPI.login({ email, password });
      localStorage.setItem('token', response.token);
      localStorage.setItem('buddy_user', JSON.stringify(response.user));
      setToken(response.token);
      setUser(response.user);
      socketService.connect(response.token, response.user.id);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const register = async (username: string, email: string, password: string): Promise<AuthResponse> => {
    try {
      const response = await authAPI.register({ username, email, password });
      localStorage.setItem('token', response.token);
      localStorage.setItem('buddy_user', JSON.stringify(response.user));
      setToken(response.token);
      setUser(response.user);
      socketService.connect(response.token, response.user.id);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('buddy_user');
    setToken(null);
    setUser(null);
    socketService.disconnect();
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('buddy_user', JSON.stringify(updatedUser));
    }
  };

  const purchaseVip = async () => {
    try {
      const { user: updatedUser } = await userAPI.purchaseVip();
      setUser(updatedUser);
      localStorage.setItem('buddy_user', JSON.stringify(updatedUser));
    } catch (error) {
      console.error('Failed to purchase VIP in backend', error);
      throw error;
    }
  };

  const addCoins = async (amount: number) => {
    try {
      const { user: updatedUser } = await userAPI.addCoins(amount);
      setUser(updatedUser);
      localStorage.setItem('buddy_user', JSON.stringify(updatedUser));
    } catch (error) {
      console.error('Failed to add coins in backend', error);
      throw error;
    }
  };

  const isVipActive = () => {
    if (!user?.isVip || !user.vipExpiresAt) return false;
    return new Date() < user.vipExpiresAt;
  };

  const getVipDaysLeft = () => {
    if (!user?.vipExpiresAt) return 0;
    const now = new Date();
    const diff = user.vipExpiresAt.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  return {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUser,
    addCoins,
    purchaseVip,
    isVipActive,
    getVipDaysLeft,
    isAuthenticated: !!user
  };
};
